// The Knowledge bank singleton tab's centre (#60): the sources list, or the
// source gone into (`selectedId`, `/knowledge/{id}`) in its viewer. The list
// projects the live Library read (knowledgeBankModel); every intent maps to
// the host's existing commands (the same lifecycle the Library rows use).

import type { ReactNode } from "react";
import {
    KnowledgeBank,
    type KnowledgeRowAction,
} from "../../../components/KnowledgeBank/KnowledgeBank.js";
import {
    type KnowledgeRow,
    knowledgeRowsOf,
} from "../../../components/KnowledgeBank/knowledgeBankModel.js";
import type {
    FocusTarget,
    ResourceRef,
} from "../../../components/Navigation/types.js";
import { uploadingFilenames } from "../../ingest.js";
import { useAppShell } from "../context.js";
import { KnowledgeItem } from "./KnowledgeItem.js";

const refOfRow = (row: KnowledgeRow): ResourceRef => ({
    type: row.kind,
    id: row.id,
    title: row.title,
});

export const KnowledgeCentre = ({
    selectedId,
    target,
}: {
    readonly selectedId?: string | undefined;
    readonly target?: FocusTarget | undefined;
}): ReactNode => {
    const {
        library,
        searchResults,
        dispatch,
        openFilePicker,
        ingest,
        submitResourceRename,
        archiveResourceNow,
        unarchiveResourceNow,
        setConfirmDelete,
    } = useAppShell();
    const rows = knowledgeRowsOf(searchResults, library);

    if (selectedId !== undefined)
        return (
            <KnowledgeItem
                id={selectedId}
                row={rows.find((r) => r.id === selectedId)}
                target={target}
            />
        );

    const onRowAction = (row: KnowledgeRow, action: KnowledgeRowAction) => {
        const ref = refOfRow(row);
        switch (action.type) {
            case "rename":
                submitResourceRename(row.id, action.title);
                return;
            case "archive":
                archiveResourceNow(ref);
                return;
            case "unarchive":
                unarchiveResourceNow(ref);
                return;
            case "delete":
                setConfirmDelete(ref);
                return;
        }
    };

    return (
        <div className="kb-centre">
            <KnowledgeBank
                rows={rows}
                waiting={library === null}
                uploading={uploadingFilenames(ingest)}
                onOpen={(row) =>
                    dispatch({ type: "open-knowledge", selectedId: row.id })
                }
                onAddSources={openFilePicker}
                onRowAction={onRowAction}
            />
        </div>
    );
};
